import { useStore } from 'zustand';
import { computeCoziness } from '@/core/domain/coziness';
import type { Game } from '@/game/Game';
import { useI18n } from '@/ui/i18n/I18nProvider';
import { usePlacementUi, useSave, useSaveDerived, useServices } from '@/ui/services/ServicesContext';
import styles from './SelectionCard.module.css';
import { Icon } from './Icon';
import { currentRoom } from '@/ui/util/room';

/** True while the selection card is up (an item is selected, not being dragged, no mini game open). */
export function useSelectionShown(game: Game): boolean {
  const selected = usePlacementUi(game, (s) => s.selected);
  const dragging = usePlacementUi(game, (s) => s.dragging);
  const miniGame = useStore(game.miniGameUi, (s) => s.run !== null || s.intro !== null);
  const placed = useSave((s) => (selected ? s.inventory.instances[selected]?.itemId !== undefined : false));
  return placed && !dragging && !miniGame;
}

/**
 * S07 Xem món + S25 toolbar (ux-screens §3.7): while an item is selected, one card takes the bag tray's place
 * at the bottom: name, ★, lore, set progress, "Đúng chỗ", then the actions (Xoay, Cất vào túi, Xong; Xếp đầy kệ
 * on a Tiệm shelf, Vào bếp on the kitchen counter). Nothing floats over the room, so the item stays in sight,
 * and the tray keeps its place underneath, so the camera never moves. Tapping elsewhere deselects.
 */
export function SelectionCard({ game }: { game: Game }) {
  const { t } = useI18n();
  const { content, dispatch } = useServices();
  const shown = useSelectionShown(game);
  const selected = usePlacementUi(game, (s) => s.selected);
  const itemId = useSave((s) => (selected ? s.inventory.instances[selected]?.itemId : undefined));
  const favorite = useSave((s) => (itemId ? s.inventory.favorites.includes(itemId) : false));
  const rightSpot = useSaveDerived((s) => {
    if (!selected) return false;
    const room = currentRoom(s);
    return computeCoziness(content, s, room).rightSpotIds.includes(selected);
  });
  const setHave = useSaveDerived((s) => {
    const def = itemId ? content.items.get(itemId) : undefined;
    const set = def?.setId ? content.sets.get(def.setId) : undefined;
    if (!set) return 0;
    const room = currentRoom(s);
    const placed = new Set(
      Object.values(s.rooms[room].placements).map((p) => s.inventory.instances[p.instanceId]?.itemId),
    );
    return set.itemIds.filter((id) => placed.has(id)).length;
  });
  const canCook = useSave(
    (s) =>
      itemId === 'fur_kitchen_counter' &&
      s.unlockedIds.includes('unl_cooking') &&
      s.lastSession.sceneId === 'home',
  );
  // "Xếp đầy kệ" (M2-05): a shelf in the Tiệm with an empty slot, and goods in the bag.
  const canFill = useSave((s) => {
    if (!selected || s.lastSession.sceneId !== 'minimart') return false;
    const shelf = content.items.get(s.inventory.instances[selected]?.itemId ?? 'dec_none');
    const slots = shelf?.slots?.count ?? 0;
    const room = s.rooms.minimart.slots;
    const free = Array.from({ length: slots }, (_, i) => `${selected}#${String(i)}`).some(
      (k) => !(k in room),
    );
    return free && Object.values(s.inventory.productStacks).some((n) => n > 0);
  });

  const def = itemId ? content.items.get(itemId) : undefined;
  if (!shown || !itemId || !def) return null;
  const set = def.setId ? content.sets.get(def.setId) : undefined;
  return (
    <section className={styles.card} aria-label={t(def.nameKey)} data-testid="inspect">
      <div className={styles.head}>
        <h2 className={styles.name}>{t(def.nameKey)}</h2>
        <button
          type="button"
          className={styles.icon}
          aria-pressed={favorite}
          aria-label={t(favorite ? 'ui.inspect.unfavorite' : 'ui.inspect.favorite')}
          data-testid="inspect-favorite"
          onClick={() => {
            dispatch({ type: 'toggleFavorite', itemId });
          }}
        >
          <Icon name="star" size={26} className={favorite ? undefined : styles.off} />
        </button>
        <button
          type="button"
          className={styles.icon}
          aria-label={t('ui.common.close')}
          onClick={() => {
            game.clearSelection();
          }}
        >
          <Icon name="close" size={22} />
        </button>
      </div>
      <p className={styles.lore}>“{t(def.loreKey)}”</p>
      {(set || rightSpot) && (
        <p className={styles.meta}>
          {set && (
            <span>
              {t('ui.catalog.set_line', { name: t(set.nameKey), have: setHave, total: set.itemIds.length })}
            </span>
          )}
          {rightSpot && <span className={styles.right}>{t('ui.inspect.right_spot')}</span>}
        </p>
      )}
      <div className={styles.tools} role="toolbar" aria-label={t(def.nameKey)} data-testid="placement-bar">
        {def.rotations > 1 && def.layer !== 'wall' && def.layer !== 'surface' && (
          <button
            type="button"
            className={styles.tool}
            data-tutorial-id="rotate"
            onClick={() => {
              game.rotateSelection();
            }}
          >
            <Icon name="rotate" size={24} />
            <span>{t('ui.place.rotate')}</span>
          </button>
        )}
        {canFill && (
          <button
            type="button"
            className={styles.tool}
            data-testid="quick-fill"
            onClick={() => {
              game.quickFillSelection();
            }}
          >
            <Icon name="packing" size={24} />
            <span>{t('ui.place.quick_fill')}</span>
          </button>
        )}
        {canCook && (
          <button
            type="button"
            className={styles.tool}
            data-testid="inspect-cook"
            onClick={() => {
              game.clearSelection();
              game.miniGames.openIntro('mg_cooking');
            }}
          >
            <Icon name="cooking" size={24} />
            <span>{t('ui.inspect.cook')}</span>
          </button>
        )}
        {def.removable && (
          <button
            type="button"
            className={styles.tool}
            onClick={() => {
              game.storeSelection();
            }}
          >
            <Icon name="store" size={24} />
            <span>{t('ui.place.store')}</span>
          </button>
        )}
        <button
          type="button"
          className={`${styles.tool} ${styles.done}`}
          onClick={() => {
            game.clearSelection();
          }}
        >
          <Icon name="check" size={24} />
          <span>{t('ui.place.done')}</span>
        </button>
      </div>
    </section>
  );
}
